const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * The closed icon set from `ICON_NAMES`. Drawn here so a new client can render
 * the same protocol without shipping an icon font. Unknown names fall through
 * to a neutral placeholder rather than disappearing.
 */
const PATHS: Record<string, string> = {
  app: "M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z",
  check: "M5 12.5 10 17.5 19 7",
  "chevron-down": "M6 9.5 12 15.5 18 9.5",
  "chevron-right": "M9.5 6 15.5 12 9.5 18",
  close: "M6 6 18 18M18 6 6 18",
  collection: "M4 8.5 12 4.5l8 4-8 4zM4 12.5l8 4 8-4M4 16.5l8 4 8-4",
  document: "M6 3h7l5 5v13H6zM13 3v5h5",
  folder: "M3.5 6.5h6l2 2.5h9v10h-17z",
  gear: "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1",
  grip: "M9 6.5h.01M15 6.5h.01M9 12h.01M15 12h.01M9 17.5h.01M15 17.5h.01",
  note: "M6 3h12v18H6zM9.5 8h5M9.5 12h5M9.5 16h3",
  object: "M12 3 20.5 7.5v9L12 21 3.5 16.5v-9zM12 3v18M3.5 7.5 12 12l8.5-4.5",
  plus: "M12 5.5v13M5.5 12h13",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M16 16l4 4",
  terminal: "M3.5 5h17v14h-17zM7 9.5l3 2.5-3 2.5M12.5 15h4",
  trash: "M4.5 6.5h15M9 6.5V4h6v2.5M6.5 6.5 7.5 20h9l1-13.5M10 10v6M14 10v6",
  warning: "M12 3.5 22 20H2zM12 9.5v5M12 17.2h.01",
};

const TONE: Record<string, string> = {
  default: "currentColor",
  muted: "var(--fg-muted)",
  accent: "var(--accent)",
  danger: "var(--danger)",
  success: "var(--success)",
};

export function createIcon(name: string, size = 16, tone = "default"): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke-width", "1.6");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  svg.style.flex = "none";
  svg.appendChild(document.createElementNS(SVG_NS, "path"));
  updateIcon(svg, name, size, tone);
  return svg;
}

export function updateIcon(svg: SVGSVGElement, name: string, size = 16, tone = "default"): void {
  const px = String(Math.max(8, Math.min(64, size)));
  if (svg.getAttribute("width") !== px) {
    svg.setAttribute("width", px);
    svg.setAttribute("height", px);
  }
  const stroke = TONE[tone] ?? "currentColor";
  if (svg.getAttribute("stroke") !== stroke) svg.setAttribute("stroke", stroke);

  const known = PATHS[name];
  const shape = svg.firstElementChild;
  if (shape === null) return;
  if (known === undefined) {
    if (svg.getAttribute("data-icon") !== "unknown") {
      svg.setAttribute("data-icon", "unknown");
      const rect = document.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", "4.5");
      rect.setAttribute("y", "4.5");
      rect.setAttribute("width", "15");
      rect.setAttribute("height", "15");
      rect.setAttribute("rx", "3");
      rect.setAttribute("stroke-dasharray", "3 2");
      svg.replaceChild(rect, shape);
    }
    return;
  }
  if (svg.getAttribute("data-icon") === name) return;
  svg.setAttribute("data-icon", name);
  if (shape.tagName !== "path") {
    svg.replaceChild(document.createElementNS(SVG_NS, "path"), shape);
  }
  svg.firstElementChild?.setAttribute("d", known);
}
